/**
 * useConfirm — Confirmation dialog hook for destructive actions.
 * Mirrors Tauri ConfirmDialog for feature parity.
 */
import { useState, useCallback } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ConfirmOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'warning' | 'info';
}

interface ConfirmState extends ConfirmOptions {
  isOpen: boolean;
  resolve: ((value: boolean) => void) | null;
}

export function useConfirm() {
  const [state, setState] = useState<ConfirmState>({
    isOpen: false,
    title: '',
    message: '',
    confirmLabel: 'Confirm',
    cancelLabel: 'Cancel',
    variant: 'danger',
    resolve: null,
  });

  const confirm = useCallback((options: ConfirmOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      setState({
        ...options,
        confirmLabel: options.confirmLabel ?? 'Confirm',
        cancelLabel: options.cancelLabel ?? 'Cancel',
        variant: options.variant ?? 'danger',
        isOpen: true,
        resolve,
      });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    state.resolve?.(true);
    setState((s) => ({ ...s, isOpen: false, resolve: null }));
  }, [state.resolve]);

  const handleCancel = useCallback(() => {
    state.resolve?.(false);
    setState((s) => ({ ...s, isOpen: false, resolve: null }));
  }, [state.resolve]);

  const dialog = state.isOpen ? (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-6 shadow-lg">
        <div className="flex items-start gap-4">
          <div className={cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full",
            state.variant === 'danger' && "bg-destructive/10",
            state.variant === 'warning' && "bg-amber-500/10",
            state.variant === 'info' && "bg-primary/10"
          )}>
            <AlertTriangle className={cn(
              "h-5 w-5",
              state.variant === 'danger' && "text-destructive",
              state.variant === 'warning' && "text-amber-500",
              state.variant === 'info' && "text-primary"
            )} />
          </div>
          <div className="flex-1">
            <h3 className="font-semibold text-lg">{state.title}</h3>
            <p className="text-sm text-muted-foreground mt-1">{state.message}</p>
          </div>
          <button
            onClick={handleCancel}
            className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-6 flex justify-end gap-2">
          <button
            onClick={handleCancel}
            className="rounded-lg border border-border px-4 py-2 text-sm hover:bg-muted transition-colors"
          >
            {state.cancelLabel}
          </button>
          <button
            onClick={handleConfirm}
            className={cn(
              "rounded-lg px-4 py-2 text-sm font-medium transition-colors",
              state.variant === 'danger' && "bg-destructive text-destructive-foreground hover:bg-destructive/90",
              state.variant === 'warning' && "bg-amber-500 text-white hover:bg-amber-500/90",
              state.variant === 'info' && "bg-primary text-primary-foreground hover:bg-primary/90"
            )}
          >
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  ) : null;

  return { confirm, dialog };
}
